/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  IonPage,
  IonContent,
} from "@ionic/react";
import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Grid,
  Divider,
  useMediaQuery,
  useTheme as useMuiTheme,
  Avatar,
  styled,
  Link as MuiLink,
  createTheme,
  Card,
  CardContent,
  CardActionArea,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import {
  ArrowBack,
  Logout,
  Print,
  Edit,
  Delete,
  Visibility,
  Close,
  Save,
  Cancel,
  VerifiedUser,
  Face,
  CalendarToday,
  Badge as BadgeIcon,
  School,
  Refresh,
  QrCode,
  ContentCopy,
  CheckCircle,
  Key,
  Search,
} from "@mui/icons-material";
import { ThemeProvider } from "@emotion/react";
import { getUser, baseUrl, requestID, config, logOutUser, getToken } from "../../common/utils";
import axios from "axios";
import * as QRCode from "qrcode.react";

// ============================================
// Modern Dashboard Theme (MUI5) - Matching DeltaGovPortal
// ============================================
const theme = createTheme({
  palette: {
    primary: {
      main: "#243b5a",
    },
    secondary: {
      main: "#ef4444",
    },
    success: {
      main: "#059669",
    },
    error: {
      main: "#dc2626",
    },
    background: {
      default: "#f4f6f8",
      paper: "#ffffff",
    },
    text: {
      primary: "#1f2937",
      secondary: "#6b7280",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: "2rem",
      fontWeight: 700,
      color: "#1f2937",
    },
    h4: {
      fontSize: "1.25rem",
      fontWeight: 600,
    },
    h5: {
      fontSize: "1.125rem",
      fontWeight: 600,
    },
    body1: {
      fontSize: "0.875rem",
      color: "#6b7280",
    },
    body2: {
      fontSize: "0.875rem",
      color: "#374151",
    },
    caption: {
      fontSize: "0.75rem",
      letterSpacing: "1px",
      color: "#6b7280",
      textTransform: "uppercase",
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 600,
      color: "#ffffff",
    },
  },
  shape: {
    borderRadius: 6,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          border: "1px solid #e5e7eb",
          boxShadow: "none",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: "1px solid #e5e7eb",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          borderRadius: "6px",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
            borderColor: "#243b5a",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          padding: "8px 16px",
          borderRadius: 6,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "#d1d5db",
          borderWidth: "1px",
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          "&.Mui-selected": {
            backgroundColor: "#243b5a",
            color: "white",
            "&:hover": {
              backgroundColor: "#162c47",
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& input": {
            textAlign: "center",
          },
        },
      },
    },
  },
});

// ============================================
// Styled Components
// ============================================
const Header = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  padding: "16px 40px",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  boxSizing: "border-box",
}));

const HeaderTopRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
});

const HeaderBottomRow = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  width: "100%",
  marginTop: "8px",
});

const LogoCircle = styled(Box)({
  width: "55px",
  height: "55px",
  borderRadius: "50%",
  backgroundColor: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "20px",
  color: "#243b5a",
  fontWeight: "bold",
  border: "2px solid #f0c45a",
  boxSizing: "border-box",
  marginRight: "16px",
});

const FooterLink = styled(MuiLink)({
  color: "#d1d5db",
  textDecoration: "underline",
  cursor: "pointer",
  "&:hover": {
    color: "#ffffff",
  },
});

const SchoolInfoBox = styled(Box)({
  textAlign: "right",
  color: "#f1f5f9",
});

const LogoutButton = styled(Button)({
  backgroundColor: "#ef4444",
  color: "#ffffff",
  fontWeight: 600,
  fontSize: "14px",
  textTransform: "none",
  padding: "6px 16px",
  borderRadius: "6px",
  "&:hover": {
    backgroundColor: "#dc2626",
  },
  "& svg": {
    marginRight: "8px",
  },
});

// QR Code Card component
const QRCodeCard = styled(Paper)(({ theme }) => ({
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  },
  "@media print": {
    border: "1px solid #000",
    boxShadow: "none",
    pageBreakInside: "avoid",
  },
}));

// Styled component for two-digit score input
const ScoreTextField = styled(TextField)({
  "& .MuiInputBase-root": {
    width: "70px",
  },
  "& .MuiInputBase-input": {
    textAlign: "center",
    padding: "8px 4px",
    fontSize: "0.875rem",
  },
});

interface Student {
  id: string | number;
  schoolId: string | number;
  firstname: string;
  othername: string;
  lastname: string;
  gender: string;
  schoolType: string;
  dateOfBirth: string;
  passport: string | null;
  englishTerm1: string;
  englishTerm2: string;
  englishTerm3: string;
  arithmeticTerm1: string;
  arithmeticTerm2: string;
  arithmeticTerm3: string;
  generalTerm1: string;
  generalTerm2: string;
  generalTerm3: string;
  religiousType: string;
  religiousTerm1: string;
  religiousTerm2: string;
  religiousTerm3: string;
  lgacode: string | number;
  year: string;
  studentNumber: string;
  accCode: string;
  regno?: number;
  lateRegistration?: number;
  createdAt?: string;
  updatedAt?: string;
  id2?: string;
  prcd?: number;
}

interface EditFormData {
  id: string;
  schoolId: string;
  firstname: string;
  othername: string;
  lastname: string;
  gender: string;
  schoolType: string;
  dateOfBirth: string;
  englishTerm1: string;
  englishTerm2: string;
  englishTerm3: string;
  arithmeticTerm1: string;
  arithmeticTerm2: string;
  arithmeticTerm3: string;
  generalTerm1: string;
  generalTerm2: string;
  generalTerm3: string;
  religiousType: string;
  religiousTerm1: string;
  religiousTerm2: string;
  religiousTerm3: string;
  studentNumber: string;
  passport?: File | null;
}

interface MessageState {
  type: 'success' | 'error' | '';
  text: string;
}

type ViewOption = 'no-passport' | 'with-passport' | 'with-dob' | 'names-access-codes';

const getReligiousTypeLabel = (type: string): string => {
  if (type === "1" || type === "Christian" || type === "christian") return "Christian";
  if (type === "2" || type === "Islam" || type === "islam" || type === "Muslim" || type === "muslim") return "Muslim";
  return type || "N/A";
};

const getCurrentSession = (): string => {
  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;
  return `${currentYear}/${nextYear}`;
};

const ValidationListPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const queryParams = new URLSearchParams(location.search);
  const urlLgaCode = queryParams.get('lgacode');
  const urlSchoolCode = queryParams.get('schoolcode');
  const urlSession = queryParams.get('session');
  const isQRView = !!(urlLgaCode && urlSchoolCode);

  const user = getUser();

  const schoolData = {
    schoolName: user?.schoolName || "",
    lgaCode: urlLgaCode || user?.lgaCode || "",
    schoolCode: urlSchoolCode || user?.schoolCode || "",
    session: urlSession || getCurrentSession(),
  };

  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [message, setMessage] = useState<MessageState>({ type: '', text: '' });
  const [isPublicView, setIsPublicView] = useState<boolean>(isQRView);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const [shareableLink, setShareableLink] = useState<string>("");
  const [viewOption, setViewOption] = useState<ViewOption>('no-passport');
  const showNamesAccessCodes = viewOption === 'names-access-codes';
  const [viewModalOpen, setViewModalOpen] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const filteredStudents = students.filter((student) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase().trim();
    const fullName = `${student.lastname || ""} ${student.firstname || ""} ${student.othername || ""}`.toLowerCase();
    const studentNumber = (student.studentNumber || "").toLowerCase();
    return fullName.includes(query) || studentNumber.includes(query);
  });

  const [editForm, setEditForm] = useState<EditFormData>({
    id: "",
    schoolId: "",
    firstname: "",
    othername: "",
    lastname: "",
    gender: "",
    schoolType: "",
    dateOfBirth: "",
    englishTerm1: "",
    englishTerm2: "",
    englishTerm3: "",
    arithmeticTerm1: "",
    arithmeticTerm2: "",
    arithmeticTerm3: "",
    generalTerm1: "",
    generalTerm2: "",
    generalTerm3: "",
    religiousType: "",
    religiousTerm1: "",
    religiousTerm2: "",
    religiousTerm3: "",
    studentNumber: "",
    passport: null,
  });

  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Generate shareable link with QR code parameters
  const generateShareableLink = () => {
    //const baseUrl = '';
    const currentPath = 'schools/validation_online/';//window.location.pathname;
    const session = schoolData.session;
    
    const link = `${baseUrl()}${currentPath}?lgacode=${schoolData.lgaCode}&schoolcode=${schoolData.schoolCode}&session=${encodeURIComponent(session)}`;
    setShareableLink(link);
    return link;
  };

  const fetchStudents = async (showRefreshing = false) => {
    if (showRefreshing) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      if (!schoolData.lgaCode || !schoolData.schoolCode) {
        throw new Error("Missing school or LGA information");
      }

      const res = await axios.post(
        `${baseUrl()}schools/getStudents`,
        {
          lgaCode: schoolData.lgaCode,
          schCode: schoolData.schoolCode,
          requestID: requestID(),
        },
        config
      );

      if (res.data.data?.students) {
        let studentsData = [];
        
        if (Array.isArray(res.data.data?.students)) {
          studentsData = res.data.data?.students;
        } else if (res.data.data?.students && Array.isArray(res.data.data?.students)) {
          studentsData = res.data.data?.students;
        } else if (res.data.status === 1 || res.data.status === true) {
          if (Array.isArray(res.data.data?.students)) {
            studentsData = res.data.data?.students;
          }
        }
        
        if (studentsData.length > 0) {
          setStudents(studentsData);
          setMessage({ type: "success", text: `Successfully loaded ${studentsData.length} students` });
        } else {
          setStudents([]);
          setMessage({ type: "error", text: "No students found" });
        }
      } else {
        setMessage({ type: "error", text: "Invalid response format" });
      }
      
      setTimeout(() => setMessage({ type: '', text: "" }), 3000);
    } catch (error) {
      console.log("Fetch error:", error);
      setMessage({ type: "error", text: "Error fetching students" });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStudents();
    generateShareableLink();
  }, []);

  const handleRefresh = () => {
    fetchStudents(true);
  };

  const handleLogout = () => {
    logOutUser();
    history.push("/login");
  };

  const handleBackToDashboard = () => {
    if (isPublicView) {
      history.push("/login");
    } else {
      history.push("/school_dashboard");
    }
  };

  const handleViewOptionChange = (
    event: React.MouseEvent<HTMLElement>,
    newOption: ViewOption | null,
  ) => {
    if (newOption !== null) {
      setViewOption(newOption);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareableLink);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
      setMessage({ type: "success", text: "Link copied to clipboard!" });
      setTimeout(() => setMessage({ type: '', text: "" }), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
      setMessage({ type: "error", text: "Failed to copy link" });
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow pop-ups to print');
      return;
    }

    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    const showPassport = viewOption === 'with-passport';
    const showDOB = viewOption === 'with-dob';
    const showNamesAccessCodes = viewOption === 'names-access-codes';
    const printTitle = showNamesAccessCodes ? 'STUDENT VALIDATION LIST WITH ACCESS CODES' : 'STUDENT VALIDATION LIST WITH ASSESSMENT SCORES';

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Validation List - ${schoolData.schoolName}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
            font-family: 'Inter', 'Roboto', 'Arial', sans-serif;
          }
          
          body {
            padding: 30px;
            background: white;
            color: #1f2937;
          }
          
          .print-header {
            text-align: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 2px solid #243b5a;
          }
          
          .print-header h1 {
            color: #243b5a;
            font-size: 24px;
            font-weight: 700;
            margin-bottom: 8px;
            letter-spacing: -0.5px;
          }
          
          .print-header h2 {
            color: #374151;
            font-size: 18px;
            font-weight: 500;
            margin-bottom: 5px;
          }
          
          .print-header .school-info {
            color: #6b7280;
            font-size: 14px;
            margin-top: 10px;
            padding-top: 10px;
            border-top: 1px dashed #d1d5db;
          }
          
          .print-date {
            color: #6b7280;
            font-size: 12px;
            margin-top: 10px;
          }
          
          .qr-section {
            margin: 20px 0;
            padding: 20px;
            background: #f9fafb;
            border-radius: 8px;
            border: 1px solid #e5e7eb;
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 20px;
          }
          
          .qr-box {
            text-align: center;
            flex-shrink: 0;
          }
          
          .qr-box img {
            width: 150px;
            height: 150px;
            border: 1px solid #d1d5db;
            padding: 10px;
            background: white;
          }
          
          .qr-info {
            flex: 1;
          }
          
          .qr-info h3 {
            color: #243b5a;
            margin-bottom: 10px;
          }
          
          .qr-info p {
            color: #6b7280;
            font-size: 12px;
            margin-bottom: 5px;
          }
          
          .view-option-badge {
            display: inline-block;
            margin-top: 10px;
            padding: 4px 12px;
            background: #f3f4f6;
            border-radius: 20px;
            font-size: 12px;
            color: #243b5a;
            font-weight: 600;
            border: 1px solid #d1d5db;
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
            font-size: 12px;
          }
          
          th {
            background: #243b5a;
            color: white;
            font-weight: 600;
            padding: 12px 8px;
            text-align: left;
            font-size: 13px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          td {
            padding: 10px 8px;
            border-bottom: 1px solid #e5e7eb;
            color: #374151;
          }
          
          tr:nth-child(even) {
            background-color: #f9fafb;
          }
          
          .gender-badge {
            display: inline-block;
            padding: 4px 8px;
            border-radius: 4px;
            font-weight: 600;
            font-size: 11px;
            text-transform: uppercase;
          }
          
          .gender-male {
            background: #e3f2fd;
            color: #1976d2;
          }
          
          .gender-female {
            background: #fce4ec;
            color: #c2185b;
          }
          
          .score-cell {
            text-align: center;
            font-weight: 500;
            background-color: #f8fafc;
          }
          
          .print-footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #e5e7eb;
            text-align: center;
            color: #6b7280;
            font-size: 11px;
          }
          
          .summary-stats {
            margin: 20px 0;
            padding: 15px;
            background: #f9fafb;
            border-radius: 8px;
            border: 1px solid #e5e7eb;
            display: flex;
            gap: 30px;
            flex-wrap: wrap;
          }
          
          .stat-item {
            display: flex;
            flex-direction: column;
          }
          
          .stat-label {
            font-size: 11px;
            color: #6b7280;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .stat-value {
            font-size: 18px;
            font-weight: 700;
            color: #243b5a;
            margin-top: 4px;
          }
          
          .no-data {
            text-align: center;
            padding: 50px;
            color: #9ca3af;
            font-style: italic;
          }
          
          @media print {
            body {
              padding: 0.5in;
            }
            
            th {
              background: #243b5a !important;
              color: white !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            
            .qr-section {
              break-inside: avoid;
              page-break-inside: avoid;
            }
          }
          
          .watermark {
            position: fixed;
            bottom: 20px;
            right: 20px;
            opacity: 0.1;
            font-size: 60px;
            font-weight: bold;
            color: #243b5a;
            transform: rotate(-15deg);
            pointer-events: none;
            z-index: 1000;
          }
        </style>
      </head>
      <body>
        <div class="watermark">VALIDATION COPY</div>
        
        <div class="print-header">
          <h1>DELTA STATE MINISTRY OF PRIMARY EDUCATION</h1>
          <h2>${printTitle}</h2>
          <h3>${schoolData.schoolName}</h3>
          <div class="school-info">
            LGA Code: ${schoolData.lgaCode} | School Code: ${schoolData.schoolCode} | Session: ${schoolData.session}
          </div>
          <div class="view-option-badge">
            View: ${viewOption === 'no-passport' ? 'Without Passport' : viewOption === 'with-passport' ? 'With Passport' : viewOption === 'with-dob' ? 'With Date of Birth' : 'Names and Access Codes'}
          </div>
          <div class="print-date">
            Printed on: ${currentDate}
          </div>
        </div>

        <!-- QR Code Section - Automatically included -->
        <div class="qr-section">
          <div class="qr-box">
            <img src="${shareableLink}" alt="QR Code" />
          </div>
          <div class="qr-info">
            <h3>Scan to View Online</h3>
            <p>Scan this QR code with your mobile device to view this validation list online</p>
            <p><strong>School:</strong> ${schoolData.schoolName}</p>
            <p><strong>Session:</strong> ${schoolData.session}</p>
            <p><strong>Link:</strong> ${shareableLink}</p>
          </div>
        </div>

        <!-- Summary Statistics -->
        <div class="summary-stats">
          <div class="stat-item">
            <span class="stat-label">Total Students</span>
            <span class="stat-value">${students.length}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Male Students</span>
            <span class="stat-value">${students.filter(s => s.gender?.toLowerCase() === 'male').length}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Female Students</span>
            <span class="stat-value">${students.filter(s => s.gender?.toLowerCase() === 'female').length}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">School Type</span>
            <span class="stat-value">${students.length > 0 ? students[0].schoolType : 'N/A'}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Christian</span>
            <span class="stat-value">${students.filter(s => getReligiousTypeLabel(s.religiousType) === 'Christian').length}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Muslim</span>
            <span class="stat-value">${students.filter(s => getReligiousTypeLabel(s.religiousType) === 'Muslim').length}</span>
          </div>
        </div>

        <!-- Students Table -->
        ${students.length > 0 ? `
          <table>
            <thead>
              <tr>
                <th>S/N</th>
                <th>Student Number</th>
                <th>Full Name</th>
                <th>Gender</th>
                <th>Religion</th>
                ${showNamesAccessCodes ? '<th>Access Code</th>' : ''}
                ${!showNamesAccessCodes ? `${showPassport ? '<th>Photo</th>' : ''}${showDOB ? '<th>Date of Birth</th>' : ''}` : ''}
                ${!showNamesAccessCodes ? '<th>English T1</th><th>English T2</th><th>English T3</th><th>Arithmetic T1</th><th>Arithmetic T2</th><th>Arithmetic T3</th><th>General T1</th><th>General T2</th><th>General T3</th><th>Religious T1</th><th>Religious T2</th><th>Religious T3</th>' : ''}
              </tr>
            </thead>
            <tbody>
              ${students.map((student, index) => {
                const genderLower = student.gender?.toLowerCase() || '';
                const genderClass = genderLower === 'male' ? 'gender-male' : 'gender-female';
                
                return `
                  <tr>
                    <td>${index + 1}</td>
                    <td><strong>${student.studentNumber}</strong></td>
                    <td>${student.lastname} ${student.firstname} ${student.othername || ''}</td>
                    <td>
                      <span class="gender-badge ${genderClass}">
                        ${student.gender}
                      </span>
                    </td>
                    <td>${getReligiousTypeLabel(student.religiousType)}</td>
                    ${showNamesAccessCodes ? `<td>${student.accCode || '-'}</td>` : ''}
                    ${!showNamesAccessCodes ? `${showPassport ? `
                      <td>
                        ${student.passport
                          ? `<img src="${baseUrl()}assets${student.passport}" alt="Passport" style="width: 30px; height: 30px; border-radius: 50%; object-fit: cover;" />`
                          : `<div style="width: 30px; height: 30px; background: #f3f4f6; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center;">${student.firstname?.[0] || ''}${student.lastname?.[0] || ''}</div>`
                        }
                      </td>
                    ` : ''}${showDOB ? `<td>${student.dateOfBirth || 'N/A'}</td>` : ''}` : ''}
                    ${!showNamesAccessCodes ? `<td class="score-cell">${student.englishTerm1 || '-'}</td>
                    <td class="score-cell">${student.englishTerm2 || '-'}</td>
                    <td class="score-cell">${student.englishTerm3 || '-'}</td>
                    <td class="score-cell">${student.arithmeticTerm1 || '-'}</td>
                    <td class="score-cell">${student.arithmeticTerm2 || '-'}</td>
                    <td class="score-cell">${student.arithmeticTerm3 || '-'}</td>
                    <td class="score-cell">${student.generalTerm1 || '-'}</td>
                    <td class="score-cell">${student.generalTerm2 || '-'}</td>
                    <td class="score-cell">${student.generalTerm3 || '-'}</td>
                    <td class="score-cell">${student.religiousTerm1 || '-'}</td>
                    <td class="score-cell">${student.religiousTerm2 || '-'}</td>
                    <td class="score-cell">${student.religiousTerm3 || '-'}</td>` : ''}
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        ` : `
          <div class="no-data">
            <p>No students registered yet.</p>
          </div>
        `}

        <!-- Signature Section -->
        <div style="margin-top: 60px; display: flex; justify-content: space-between;">
          <div style="text-align: center; width: 200px;">
            <div style="margin-bottom: 40px;">_________________________</div>
            <div><strong>Head Teacher's Signature</strong></div>
          </div>
          <div style="text-align: center; width: 200px;">
            <div style="margin-bottom: 40px;">_________________________</div>
            <div><strong>Validation Officer's Signature</strong></div>
          </div>
          <div style="text-align: center; width: 200px;">
            <div style="margin-bottom: 40px;">_________________________</div>
            <div><strong>Ministry Official's Signature</strong></div>
          </div>
        </div>

        <div class="print-footer">
          <p>This is an official validation document of the Delta State Government, Ministry of Primary Education</p>
          <p>Generated on: ${currentDate}</p>
          <p>Document ID: VAL-${new Date().getTime()}</p>
        </div>
        
        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() {
              window.close();
            };
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  const handleViewStudent = (student: Student) => {
    setSelectedStudent(student);
    setViewModalOpen(true);
  };

  const handleEditStudent = (student: Student) => {
    if (isPublicView) {
      setMessage({ type: "error", text: "Editing is not available in public view" });
      return;
    }
    const rawDob = student.dateOfBirth?.split(" ")[0] || "";
    const formattedDob = rawDob.includes("/") ? rawDob.split("/").reverse().join("-") : rawDob;
    setSelectedStudent(student);
    setEditForm({
      id: String(student.id),
      schoolId: String(student.schoolId),
      firstname: student.firstname || "",
      othername: student.othername || "",
      lastname: student.lastname || "",
      gender: student.gender || "",
      schoolType: student.schoolType || "",
      dateOfBirth: formattedDob,
      englishTerm1: student.englishTerm1 || "",
      englishTerm2: student.englishTerm2 || "",
      englishTerm3: student.englishTerm3 || "",
      arithmeticTerm1: student.arithmeticTerm1 || "",
      arithmeticTerm2: student.arithmeticTerm2 || "",
      arithmeticTerm3: student.arithmeticTerm3 || "",
      generalTerm1: student.generalTerm1 || "",
      generalTerm2: student.generalTerm2 || "",
      generalTerm3: student.generalTerm3 || "",
      religiousType: student.religiousType === "1" ? "Christian" : student.religiousType === "2" ? "Islam" : student.religiousType,
      religiousTerm1: student.religiousTerm1 || "",
      religiousTerm2: student.religiousTerm2 || "",
      religiousTerm3: student.religiousTerm3 || "",
      studentNumber: student.studentNumber || "",
      passport: null,
    });
    setImagePreview(student.passport ? `${baseUrl()}assets${student.passport}` : null);
    setEditModalOpen(true);
  };

  const handleDeleteStudent = async (id: string | number) => {
    if (isPublicView) {
      setMessage({ type: "error", text: "Deletion is not available in public view" });
      return;
    }
    
    if (!window.confirm("Are you sure you want to delete this student?")) return;

    try {
      if (!user) throw new Error("No user data");

      const res = await axios.post(
        `${baseUrl()}schools/deleteStudent`,
        {
          studentId: String(id),
          schCode: user.schoolCode,
          lgaCode: user.lgaCode,
          requestID: requestID(),
        },
        config
      );

      if (res.data.status === 1 || res.data.status === true) {
        setStudents((prev) => prev.filter((student) => String(student.id) !== String(id)));
        setMessage({ type: "success", text: "Student deleted successfully" });
        setTimeout(() => setMessage({ type: '', text: "" }), 3000);
      } else {
        alert(res.data.message);
      }
    } catch (error) {
      console.log("Delete error:", error);
      setMessage({ type: "error", text: "Error deleting student" });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setEditForm((prev) => ({ ...prev, passport: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditChange = (field: keyof EditFormData, value: string) => {
    if (field.includes('Term')) {
      if (value === '' || (/^\d{0,2}$/.test(value) && (value === '' || parseInt(value) <= 99))) {
        setEditForm((prev) => ({ ...prev, [field]: value }));
      }
    } else {
      setEditForm((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleEditSubmit = async () => {
    if (isPublicView) {
      setMessage({ type: "error", text: "Editing is not available in public view" });
      return;
    }
    
    try {
      if (!user) throw new Error("No user data");

      const formData = new FormData();
      formData.append("studentId", editForm.id);
      formData.append("schoolId", editForm.schoolId);
      formData.append("firstname", editForm.firstname);
      formData.append("lastname", editForm.lastname);
      formData.append("othername", editForm.othername);
      formData.append("gender", editForm.gender);
      formData.append("schoolType", editForm.schoolType);
      formData.append("dateOfBirth", editForm.dateOfBirth);
      formData.append("englishTerm1", editForm.englishTerm1);
      formData.append("englishTerm2", editForm.englishTerm2);
      formData.append("englishTerm3", editForm.englishTerm3);
      formData.append("arithmeticTerm1", editForm.arithmeticTerm1);
      formData.append("arithmeticTerm2", editForm.arithmeticTerm2);
      formData.append("arithmeticTerm3", editForm.arithmeticTerm3);
      formData.append("generalTerm1", editForm.generalTerm1);
      formData.append("generalTerm2", editForm.generalTerm2);
      formData.append("generalTerm3", editForm.generalTerm3);
      
      const relType = editForm.religiousType === "Christian" ? "1" : editForm.religiousType === "Islam" ? "2" : editForm.religiousType;
      formData.append("religiousType", relType);
      
      formData.append("religiousTerm1", editForm.religiousTerm1);
      formData.append("religiousTerm2", editForm.religiousTerm2);
      formData.append("religiousTerm3", editForm.religiousTerm3);
      formData.append("lgaCode", user.lgaCode);
      formData.append("requestID", requestID());

      if (editForm.passport) {
        formData.append("passport", editForm.passport);
      }

      const res = await axios.post(
        `${baseUrl()}schools/updateStudent`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`
          },
        }
      );

      if (res.data.status === 1 || res.data.status === true) {
        await fetchStudents();
        setEditModalOpen(false);
        setMessage({ type: "success", text: "Student updated successfully" });
        setTimeout(() => setMessage({ type: '', text: "" }), 3000);
      } else {
        alert(res.data.message || "Failed to update student");
      }
    } catch (err: any) {
      console.error(err);
      setMessage({ type: "error", text: "Error updating student" });
    }
  };

  const getGenderColor = (gender: string) => {
    const g = gender?.toLowerCase() || "";
    return g === "male" ? "#2196f3" : "#e91e63";
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <IonPage>
        <IonContent>
          <Box sx={{
            bgcolor: "background.default",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          }}>
            <Header>
              <HeaderTopRow>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <LogoCircle>⚖️</LogoCircle>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#cbd5e1", letterSpacing: "0.5px", display: "block" }}>
                      DELTA STATE GOVERNMENT
                    </Typography>
                    <Typography variant="h6" sx={{ color: "#ffffff", fontWeight: 600, lineHeight: 1.2 }}>
                      Ministry of Primary Education
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Validation List with Assessment Scores
                    </Typography>
                  </Box>
                </Box>

                <SchoolInfoBox>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, color: "#ffffff", textTransform: "uppercase" }}
                  >
                    {schoolData.schoolName || "School Name"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                    LGA: {schoolData.lgaCode} | Code: {schoolData.schoolCode}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#d1d5db", fontSize: "0.7rem" }}>
                    Session: {schoolData.session}
                  </Typography>
                  {isPublicView && (
                    <Chip
                      label="Public View"
                      size="small"
                      sx={{
                        mt: 1,
                        bgcolor: "#ff9800",
                        color: "white",
                        fontSize: "0.7rem",
                        height: "20px"
                      }}
                    />
                  )}
                </SchoolInfoBox>
              </HeaderTopRow>

              {!isPublicView && (
                <HeaderBottomRow>
                  <LogoutButton
                    variant="contained"
                    onClick={handleLogout}
                    startIcon={<Logout />}
                  >
                    Logout
                  </LogoutButton>
                </HeaderBottomRow>
              )}
            </Header>

            <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, flex: 1 }}>
              <Button
                onClick={handleBackToDashboard}
                startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
                sx={{
                  color: "#243b5a",
                  fontSize: "13px",
                  textTransform: "none",
                  p: 0,
                  mb: 3,
                  '&:hover': {
                    textDecoration: "underline",
                    backgroundColor: "transparent",
                  },
                }}
              >
                {isPublicView ? "Back to Login" : "Back to Dashboard"}
              </Button>

              {/* QR Code Section - Always Visible */}
              <QRCodeCard sx={{ mb: 4, p: 3 }}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md="auto" sx={{ textAlign: "center" }}>
                    <Box sx={{ 
                      display: "inline-block", 
                      p: 2, 
                      bgcolor: "white", 
                      borderRadius: 2,
                      border: "1px solid #e5e7eb"
                    }}>
                      {shareableLink && (
                        <QRCode.QRCodeCanvas
                          value={shareableLink}
                          size={150}
                          level="H"
                          includeMargin={true}
                        />
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md>
                    <Box>
                      <Typography variant="h6" sx={{ color: "#243b5a", mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                        <QrCode /> Scan to View Online
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Scan this QR code with your mobile device to view this validation list online. 
                        Share this code with parents and stakeholders for easy access.
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={copySuccess ? <CheckCircle /> : <ContentCopy />}
                          onClick={handleCopyLink}
                          sx={{ textTransform: "none" }}
                        >
                          {copySuccess ? "Copied!" : "Copy Link"}
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
                          {shareableLink}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </QRCodeCard>

              {/* Header Section with Actions */}
              <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 2,
                    mb: 3,
                  }}
                >
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: "#1d3557", mb: 1 }}>
                      Validation List with Assessment Scores
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      View, verify and manage registered students with their termly assessment scores
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Refresh />}
                      onClick={handleRefresh}
                      disabled={loading || refreshing}
                      sx={{
                        color: "#243b5a",
                        borderColor: "#243b5a",
                        textTransform: "none",
                      }}
                    >
                      {refreshing ? "Refreshing..." : "Refresh"}
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Print />}
                      onClick={handlePrint}
                      sx={{
                        bgcolor: "#243b5a",
                        "&:hover": { bgcolor: "#162c47" },
                        textTransform: "none",
                        px: 3,
                      }}
                    >
                      Print Validation List
                    </Button>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#4b5563" }}>
                      Display Options:
                    </Typography>
                    <ToggleButtonGroup
                      value={viewOption}
                      exclusive
                      onChange={handleViewOptionChange}
                      size="small"
                      sx={{
                        bgcolor: "#f9fafb",
                        "& .MuiToggleButton-root": {
                          px: 3,
                          py: 1,
                        },
                      }}
                    >
                      <ToggleButton value="no-passport">
                        <BadgeIcon sx={{ mr: 1, fontSize: 18 }} />
                        Without Passport
                      </ToggleButton>
                      <ToggleButton value="with-passport">
                        <Face sx={{ mr: 1, fontSize: 18 }} />
                        With Passport
                      </ToggleButton>
                      <ToggleButton value="with-dob">
                        <CalendarToday sx={{ mr: 1, fontSize: 18 }} />
                        With Date of Birth
                      </ToggleButton>
                      <ToggleButton value="names-access-codes">
                        <Key sx={{ mr: 1, fontSize: 18 }} />
                        Names and Access Codes
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  <TextField
                    placeholder="Search by name or student number..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    size="small"
                    sx={{
                      minWidth: 280,
                      "& input": { textAlign: "left" }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: "#6b7280", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Paper>

              {message.text && (
                <Alert
                  severity={message.type === 'success' ? 'success' : 'error'}
                  sx={{ mb: 3 }}
                  onClose={() => setMessage({ type: '', text: '' })}
                >
                  {message.text}
                </Alert>
              )}

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Paper sx={{ border: "1px solid #e0e0e0", overflow: "hidden", borderRadius: "8px" }}>
                  <TableContainer sx={{ maxHeight: "600px", overflow: "auto" }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          {viewOption === 'with-passport' && !showNamesAccessCodes && <TableCell rowSpan={2}><b>Photo</b></TableCell>}
                          <TableCell rowSpan={showNamesAccessCodes ? 1 : 2}><b>S/N</b></TableCell>
                          <TableCell rowSpan={showNamesAccessCodes ? 1 : 2}><b>Student Number</b></TableCell>
                          <TableCell rowSpan={showNamesAccessCodes ? 1 : 2}><b>Full Name</b></TableCell>
                          <TableCell rowSpan={showNamesAccessCodes ? 1 : 2}><b>Gender</b></TableCell>
                          <TableCell rowSpan={showNamesAccessCodes ? 1 : 2}><b>Religion</b></TableCell>
                          {viewOption === 'names-access-codes' && <TableCell rowSpan={1}><b>Access Code</b></TableCell>}
                          {!showNamesAccessCodes && (
                            <>
                              <TableCell colSpan={3} align="center" sx={{ borderLeft: "1px solid #e0e0e0" }}><b>English</b></TableCell>
                              <TableCell colSpan={3} align="center"><b>Arithmetic</b></TableCell>
                              <TableCell colSpan={3} align="center"><b>General Paper</b></TableCell>
                              <TableCell colSpan={3} align="center"><b>Religious Education</b></TableCell>
                            </>
                          )}
                          {!isPublicView && <TableCell rowSpan={showNamesAccessCodes ? 1 : 2} align="center"><b>Actions</b></TableCell>}
                        </TableRow>
                        {!showNamesAccessCodes && (
                          <TableRow>
                            <TableCell align="center" sx={{ borderLeft: "1px solid #e0e0e0" }}><b>T1</b></TableCell>
                            <TableCell align="center"><b>T2</b></TableCell>
                            <TableCell align="center"><b>T3</b></TableCell>
                            <TableCell align="center"><b>T1</b></TableCell>
                            <TableCell align="center"><b>T2</b></TableCell>
                            <TableCell align="center"><b>T3</b></TableCell>
                            <TableCell align="center"><b>T1</b></TableCell>
                            <TableCell align="center"><b>T2</b></TableCell>
                            <TableCell align="center"><b>T3</b></TableCell>
                            <TableCell align="center"><b>T1</b></TableCell>
                            <TableCell align="center"><b>T2</b></TableCell>
                            <TableCell align="center"><b>T3</b></TableCell>
                          </TableRow>
                        )}
                      </TableHead>

                      <TableBody>
                        {filteredStudents.length > 0 ? (
                          filteredStudents.map((student, index) => {
                            return (
                              <TableRow key={student.id || index} hover>
                                {viewOption === 'with-passport' && !showNamesAccessCodes && (
                                  <TableCell>
                                    <Avatar
                                      src={student.passport ? `${baseUrl()}assets${student.passport}` : ''}
                                      alt={`${student.firstname} ${student.lastname}`}
                                      sx={{ width: 45, height: 45 }}
                                    >
                                      {!student.passport && (student.firstname?.[0] || '') + (student.lastname?.[0] || '')}
                                    </Avatar>
                                  </TableCell>
                                )}
                                <TableCell>{index + 1}</TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="500">
                                    {student.studentNumber}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {student.lastname} {student.firstname} {student.othername || ""}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={student.gender}
                                    size="small"
                                    sx={{
                                      bgcolor: getGenderColor(student.gender),
                                      color: "white",
                                      fontSize: "0.75rem",
                                      minWidth: 60,
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={getReligiousTypeLabel(student.religiousType)}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      fontSize: "0.75rem",
                                      bgcolor: getReligiousTypeLabel(student.religiousType) === 'Christian' ? "#fef3c7" : "#e0f2fe",
                                    }}
                                  />
                                </TableCell>
                                {viewOption === 'names-access-codes' && (
                                  <TableCell>
                                    <Typography variant="body2">{student.accCode || '-'}</Typography>
                                  </TableCell>
                                )}
                                {viewOption === 'with-dob' && !showNamesAccessCodes && (
                                  <TableCell>
                                    <Typography variant="body2">
                                      {formatDate(student.dateOfBirth)}
                                    </Typography>
                                  </TableCell>
                                )}
                                {!showNamesAccessCodes && (
                                  <>
                                    <TableCell align="center" sx={{ borderLeft: "1px solid #e0e0e0" }}>{student.englishTerm1 || '-'}</TableCell>
                                    <TableCell align="center">{student.englishTerm2 || '-'}</TableCell>
                                    <TableCell align="center">{student.englishTerm3 || '-'}</TableCell>
                                    <TableCell align="center">{student.arithmeticTerm1 || '-'}</TableCell>
                                    <TableCell align="center">{student.arithmeticTerm2 || '-'}</TableCell>
                                    <TableCell align="center">{student.arithmeticTerm3 || '-'}</TableCell>
                                    <TableCell align="center">{student.generalTerm1 || '-'}</TableCell>
                                    <TableCell align="center">{student.generalTerm2 || '-'}</TableCell>
                                    <TableCell align="center">{student.generalTerm3 || '-'}</TableCell>
                                    <TableCell align="center">{student.religiousTerm1 || '-'}</TableCell>
                                    <TableCell align="center">{student.religiousTerm2 || '-'}</TableCell>
                                    <TableCell align="center">{student.religiousTerm3 || '-'}</TableCell>
                                  </>
                                )}
                                {!isPublicView && (
                                  <TableCell align="center">
                                    <Tooltip title="View Details" arrow>
                                      <IconButton
                                        color="primary"
                                        onClick={() => handleViewStudent(student)}
                                        sx={{ bgcolor: "#e3f2fd", "&:hover": { bgcolor: "#bbdefb" }, mr: 1 }}
                                      >
                                        <Visibility fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit Student" arrow>
                                      <IconButton
                                        color="secondary"
                                        onClick={() => handleEditStudent(student)}
                                        sx={{ bgcolor: "#e8f5e9", "&:hover": { bgcolor: "#c8e6c9" }, mr: 1 }}
                                      >
                                        <Edit fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete Student" arrow>
                                      <IconButton
                                        color="error"
                                        onClick={() => handleDeleteStudent(student.id)}
                                        sx={{ bgcolor: "#ffebee", "&:hover": { bgcolor: "#ffcdd2" } }}
                                      >
                                        <Delete fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell colSpan={!isPublicView 
                              ? (viewOption === 'with-passport' ? 18 : viewOption === 'with-dob' ? 17 : viewOption === 'names-access-codes' ? 7 : 16)
                              : (viewOption === 'with-passport' ? 17 : viewOption === 'with-dob' ? 16 : viewOption === 'names-access-codes' ? 6 : 15)
                            } align="center" sx={{ py: 4 }}>
                              <Box sx={{ textAlign: "center" }}>
                                <School sx={{ fontSize: 48, color: "#d1d5db", mb: 2 }} />
                                <Typography color="text.secondary" gutterBottom>
                                  {students.length > 0 ? "No matching students found." : "No students registered yet."}
                                </Typography>
                                {!isPublicView && (
                                  <Button
                                    variant="contained"
                                    startIcon={<VerifiedUser />}
                                    onClick={() => history.push("/school-registration")}
                                    sx={{ mt: 2, bgcolor: "#243b5a" }}
                                  >
                                    Register First Student
                                  </Button>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}

              {/* View Modal */}
              <Dialog open={viewModalOpen} onClose={() => setViewModalOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ bgcolor: "#243b5a", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center", py: 2 }}>
                  <Typography variant="h6">Student Details with Assessment Scores</Typography>
                  <IconButton onClick={() => setViewModalOpen(false)} sx={{ color: "white" }}>
                    <Close />
                  </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                  {selectedStudent && (
                    <Grid container spacing={3}>
                      <Grid item xs={12} display="flex" justifyContent="center">
                        <Avatar
                          src={selectedStudent.passport ? `${baseUrl()}assets${selectedStudent.passport}` : ''}
                          alt={`${selectedStudent.firstname} ${selectedStudent.lastname}`}
                          sx={{ width: 120, height: 120, border: "3px solid #243b5a" }}
                        >
                          {!selectedStudent.passport && (selectedStudent.firstname?.[0] || '') + (selectedStudent.lastname?.[0] || '')}
                        </Avatar>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider><Chip label="Personal Information" size="small" /></Divider>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Full Name</Typography>
                        <Typography variant="body1" fontWeight="500">
                          {selectedStudent.lastname} {selectedStudent.firstname} {selectedStudent.othername}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Student Number</Typography>
                        <Typography variant="body1" fontWeight="500">{selectedStudent.studentNumber}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Gender</Typography>
                        <Chip label={selectedStudent.gender} size="small" sx={{ bgcolor: getGenderColor(selectedStudent.gender), color: "white", mt: 0.5 }} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Religious Type</Typography>
                        <Chip label={getReligiousTypeLabel(selectedStudent.religiousType)} size="small" variant="outlined" sx={{ mt: 0.5 }} />
                      </Grid>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }}><Chip label="Academic Scores (Term 1, 2, 3)" size="small" /></Divider>
                      </Grid>
                      <Grid item xs={12}>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                              <TableRow>
                                <TableCell><b>Subject</b></TableCell>
                                <TableCell align="center"><b>Term 1</b></TableCell>
                                <TableCell align="center"><b>Term 2</b></TableCell>
                                <TableCell align="center"><b>Term 3</b></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow>
                                <TableCell>English Studies</TableCell>
                                <TableCell align="center"><b>{selectedStudent.englishTerm1 || "-"}</b></TableCell>
                                <TableCell align="center"><b>{selectedStudent.englishTerm2 || "-"}</b></TableCell>
                                <TableCell align="center"><b>{selectedStudent.englishTerm3 || "-"}</b></TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Arithmetic</TableCell>
                                <TableCell align="center"><b>{selectedStudent.arithmeticTerm1 || "-"}</b></TableCell>
                                <TableCell align="center"><b>{selectedStudent.arithmeticTerm2 || "-"}</b></TableCell>
                                <TableCell align="center"><b>{selectedStudent.arithmeticTerm3 || "-"}</b></TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>General Paper</TableCell>
                                <TableCell align="center"><b>{selectedStudent.generalTerm1 || "-"}</b></TableCell>
                                <TableCell align="center"><b>{selectedStudent.generalTerm2 || "-"}</b></TableCell>
                                <TableCell align="center"><b>{selectedStudent.generalTerm3 || "-"}</b></TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Religious Education ({getReligiousTypeLabel(selectedStudent.religiousType)})</TableCell>
                                <TableCell align="center"><b>{selectedStudent.religiousTerm1 || "-"}</b></TableCell>
                                <TableCell align="center"><b>{selectedStudent.religiousTerm2 || "-"}</b></TableCell>
                                <TableCell align="center"><b>{selectedStudent.religiousTerm3 || "-"}</b></TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    </Grid>
                  )}
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: "1px solid #e0e0e0" }}>
                  <Button onClick={() => setViewModalOpen(false)} variant="contained" sx={{ bgcolor: "#243b5a", textTransform: "none", px: 4 }}>
                    Close
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Edit Modal */}
              {!isPublicView && (
                <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="md" fullWidth>
                  <DialogTitle sx={{ bgcolor: "#243b5a", color: "white", py: 2 }}>
                    <Typography variant="h6">Edit Student Record with Assessment Scores</Typography>
                  </DialogTitle>
                  <DialogContent sx={{ pt: 3 }}>
                    {selectedStudent && (
                      <Stack spacing={2.5} sx={{ mt: 1 }}>
                        <Typography fontWeight="600" sx={{ color: "#243b5a" }}>Personal Information</Typography>
                        
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, mb: 1 }}>
                          <Avatar
                            src={imagePreview || ""}
                            sx={{ width: 100, height: 100, border: "2px solid #e0e0e0", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
                          >
                            {!imagePreview && (editForm.firstname?.[0] || "") + (editForm.lastname?.[0] || "")}
                          </Avatar>
                          <input
                            type="file"
                            accept="image/*"
                            hidden
                            ref={fileInputRef}
                            onChange={handleFileChange}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Face />}
                            onClick={() => fileInputRef.current?.click()}
                            sx={{ textTransform: "none", borderRadius: "20px" }}
                          >
                            {imagePreview ? "Change Photo" : "Upload Photo"}
                          </Button>
                        </Box>

                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField label="First Name" value={editForm.firstname} onChange={(e) => handleEditChange("firstname", e.target.value)} fullWidth size="small" />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField label="Last Name" value={editForm.lastname} onChange={(e) => handleEditChange("lastname", e.target.value)} fullWidth size="small" />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField label="Other Name" value={editForm.othername} onChange={(e) => handleEditChange("othername", e.target.value)} fullWidth size="small" />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField label="Student Number" value={editForm.studentNumber} InputProps={{ readOnly: true }} fullWidth size="small" />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Gender</InputLabel>
                              <Select value={editForm.gender} label="Gender" onChange={(e: SelectChangeEvent) => handleEditChange("gender", e.target.value)}>
                                <MenuItem value="Male">Male</MenuItem>
                                <MenuItem value="Female">Female</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Religious Type</InputLabel>
                              <Select value={editForm.religiousType} label="Religious Type" onChange={(e: SelectChangeEvent) => handleEditChange("religiousType", e.target.value)}>
                                <MenuItem value="Christian">Christian</MenuItem>
                                <MenuItem value="Islam">Islam</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Date of Birth"
                              type="date"
                              value={editForm.dateOfBirth}
                              onChange={(e) => handleEditChange("dateOfBirth", e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              size="small"
                            />
                          </Grid>
                        </Grid>
                        <Divider />
                        <Typography fontWeight="600" sx={{ color: "#243b5a" }}>English Studies</Typography>
                        <Grid container spacing={2}>
                          {[1, 2, 3].map((term) => (
                            <Grid item xs={4} key={`english-${term}`}>
                              <ScoreTextField label={`Term ${term}`} value={editForm[`englishTerm${term}` as keyof EditFormData] as string} onChange={(e) => handleEditChange(`englishTerm${term}` as keyof EditFormData, e.target.value)} type="number" size="small" inputProps={{ min: 0, max: 99, maxLength: 2, style: { textAlign: 'center' } }} />
                            </Grid>
                          ))}
                        </Grid>
                        <Typography fontWeight="600" sx={{ color: "#243b5a" }}>Arithmetic</Typography>
                        <Grid container spacing={2}>
                          {[1, 2, 3].map((term) => (
                            <Grid item xs={4} key={`arithmetic-${term}`}>
                              <ScoreTextField label={`Term ${term}`} value={editForm[`arithmeticTerm${term}` as keyof EditFormData] as string} onChange={(e) => handleEditChange(`arithmeticTerm${term}` as keyof EditFormData, e.target.value)} type="number" size="small" inputProps={{ min: 0, max: 99, maxLength: 2, style: { textAlign: 'center' } }} />
                            </Grid>
                          ))}
                        </Grid>
                        <Typography fontWeight="600" sx={{ color: "#243b5a" }}>General Paper</Typography>
                        <Grid container spacing={2}>
                          {[1, 2, 3].map((term) => (
                            <Grid item xs={4} key={`general-${term}`}>
                              <ScoreTextField label={`Term ${term}`} value={editForm[`generalTerm${term}` as keyof EditFormData] as string} onChange={(e) => handleEditChange(`generalTerm${term}` as keyof EditFormData, e.target.value)} type="number" size="small" inputProps={{ min: 0, max: 99, maxLength: 2, style: { textAlign: 'center' } }} />
                            </Grid>
                          ))}
                        </Grid>
                        <Typography fontWeight="600" sx={{ color: "#243b5a" }}>Religious Education</Typography>
                        <Grid container spacing={2}>
                          {[1, 2, 3].map((term) => (
                            <Grid item xs={4} key={`religious-${term}`}>
                              <ScoreTextField label={`Term ${term}`} value={editForm[`religiousTerm${term}` as keyof EditFormData] as string} onChange={(e) => handleEditChange(`religiousTerm${term}` as keyof EditFormData, e.target.value)} type="number" size="small" inputProps={{ min: 0, max: 99, maxLength: 2, style: { textAlign: 'center' } }} />
                            </Grid>
                          ))}
                        </Grid>
                      </Stack>
                    )}
                  </DialogContent>
                  <DialogActions sx={{ p: 3, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                    <Button onClick={() => setEditModalOpen(false)} variant="outlined" startIcon={<Cancel />} sx={{ textTransform: "none" }}>Cancel</Button>
                    <Button onClick={handleEditSubmit} variant="contained" startIcon={<Save />} sx={{ bgcolor: "#243b5a", textTransform: "none" }}>Save Changes</Button>
                  </DialogActions>
                </Dialog>
              )}
            </Container>

            {/* FOOTER */}
            <Box component="footer" sx={{ bgcolor: "primary.main", color: "#d1d5db", py: { xs: 5, md: 6 }, mt: "auto" }}>
              <Container maxWidth="lg">
                <Grid container spacing={4} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>MINISTRY OF PRIMARY EDUCATION</Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>Delta State Government</Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>Asaba, Delta State, Nigeria</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>CONTACT INFORMATION</Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>Email: info@dsgmope.ng</Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>Telephone: +234 (0)901 234 5678</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>OFFICIAL RESOURCES</Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}><FooterLink href="#">www.dsgmope.ng</FooterLink></Typography>
                  </Grid>
                </Grid>
                <Divider sx={{ borderColor: "rgba(255,255,255,0.2)", my: 3 }} />
                <Typography variant="body2" align="center" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem" }}>
                  © 2026 Delta State Government, Ministry of Primary Education. All rights reserved.
                </Typography>
              </Container>
            </Box>
          </Box>
        </IonContent>
      </IonPage>
    </ThemeProvider>
  );
};

export default ValidationListPage;